import { useRef } from "react";
import { ResultOf } from "gql.tada";
import { IntroBenefitsSectionById } from "@queries/entries/IntroBenefitsSectionById";
import { useTransform, motion, MotionValue } from "motion/react";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { isVideo } from "@utils/isVideo";
import { useIsOnScreenEffect } from "@hooks/useIsOnScreenEffect";

export const PARALLAX_RATE = 0.25;

export const IntroBenefitPart = ({
  image,
  index,
  total,
  value,
  benefitItem,
}: {
  value: MotionValue<number>; // 0 to total
  image: string;
  total: number;
  index: number;
  isDesktop: boolean;
  benefitItem: NonNullable<
    NonNullable<
      ResultOf<typeof IntroBenefitsSectionById>["introBenefitsSection"]
    >["benefitItemsCollection"]
  >["items"][number];
  title: string;
}) => {
  const isMobile = !useIsDesktop();
  const isLastItem = total - 1 === index;
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const valueMap = useTransform(value, [0, index, index + 1], [0, 0, 1]);
  const y = useTransform(() => {
    if (!isLastItem) return `-${valueMap.get() * PARALLAX_RATE * 100}%`;
    else return `${valueMap.get() * (1 - PARALLAX_RATE) * 100}%`;
  });

  // Handle video play/pause based on screen visibility
  useIsOnScreenEffect(
    sectionRef,
    (isOnScreen) => {
      const element = videoRef.current;
      if (!(element instanceof HTMLVideoElement)) return;
      if (isOnScreen) element.play();
      else element.pause();
    },
    []
  );

  // Ensure video loops properly
  const handleVideoEnded = () => {
    const element = videoRef.current;
    if (element instanceof HTMLVideoElement) {
      element.currentTime = 0;
      element.play();
    }
  };

  const backgroundVideo = benefitItem?.backgroundVideo;
  const hasVideo = backgroundVideo && 
                   typeof backgroundVideo === 'object' && 
                   'url' in backgroundVideo && 
                   'contentType' in backgroundVideo &&
                   typeof backgroundVideo.url === 'string' &&
                   typeof backgroundVideo.contentType === 'string' &&
                   backgroundVideo.url.length > 0 &&
                   isVideo({ contentType: backgroundVideo.contentType });

  return (
    <div
      className="overflow-hidden sticky top-0 w-full h-screen"
      ref={sectionRef}
    >
      {hasVideo ? (
        <motion.video
          ref={videoRef}
          src={(backgroundVideo && 'url' in backgroundVideo && typeof backgroundVideo.url === 'string' ? backgroundVideo.url : '') || ""}
          autoPlay
          muted
          loop
          playsInline
          onEnded={handleVideoEnded}
          style={{ y: isMobile ? 0 : y }}
          className="size-full object-cover sticky top-0"
        />
      ) : (
        <motion.img
          src={image || ""}
          alt={`Image ${index + 1}`}
          style={{ y: isMobile ? 0 : y }}
          className="size-full object-cover sticky top-0"
        />
      )}
    </div>
  );
};
